import { useEffect } from 'react'
import { useSiteContext } from '@contexts/MkSiteContext'
import { DataStore } from '@utils/DataStore'
import { fetchRegionList, fetchConfigV2, configKey } from '@api/MkApi'
import { brandDisplayName as brandName, Brand, brandSiteKey } from '@typescript'
import { configToRegionPopupProps } from '@utils/configToRegionPopupProps'
import { CFG_REGION_POPUP, FALLBACK_REGIONS } from '@constants'

/**
 * Hook to preload region list data in the background and cache it in session storage.
 * This improves user experience by having region data ready when needed for location search.
 */
export const usePreloadRegionList = () => {
  const { themeBrand: brand } = useSiteContext()

  useEffect(() => {
    if (!brand) return

    const preloadRegionData = async () => {
      try {
        // Check if region data is already cached
        const sessionRegionList = DataStore.getItem(`${brandName(brand)}_regionList`)
        const sessionRegionPopup = DataStore.getItem(`${brandName(brand)}_regionPopup`)

        // Only preload if not already cached
        if (!sessionRegionList || !sessionRegionPopup) {
          console.log(`[usePreloadRegionList] Preloading region data for brand: ${brand}`)

          // Try primary brand first
          let regionData = await fetchRegionList(brand)

          // If primary brand fails, try fallback to clubwyndham
          if (!regionData || !Array.isArray(regionData) || regionData.length === 0) {
            console.warn(`[usePreloadRegionList] Primary brand ${brandName(brand)} failed, trying clubwyndham fallback`)
            regionData = await fetchRegionList('cw')
          }

          // If clubwyndham also fails, try other brands
          if (!regionData || !Array.isArray(regionData) || regionData.length === 0) {
            console.warn(`[usePreloadRegionList] Clubwyndham fallback failed, trying worldmark`)
            regionData = await fetchRegionList('wm')
          }

          // Only cache if we got valid data
          if (regionData && Array.isArray(regionData) && regionData.length > 0) {
            DataStore.setItem(`${brandName(brand)}_regionList`, regionData)
          } else {
            console.warn(`[usePreloadRegionList] All API attempts failed for brand: ${brand}, using comprehensive fallback data`)
            // Provide comprehensive fallback region data for better UX
            DataStore.setItem(`${brandName(brand)}_regionList`, FALLBACK_REGIONS)
            console.log(`[usePreloadRegionList] Cached comprehensive fallback region data for brand: ${brand}`)
          }

          // Preload region popup configuration
          try {
            const configData = await fetchConfigV2(configKey(brandSiteKey(Brand.fromShortName(brand)), CFG_REGION_POPUP))
            const popupProps = configToRegionPopupProps(configData)
            DataStore.setItem(`${brandName(brand)}_regionPopup`, popupProps)
          } catch (configError) {
            console.warn(`[usePreloadRegionList] Failed to load region popup config for brand ${brand}:`, configError)
          }
        }
      } catch (error) {
        console.warn(`[usePreloadRegionList] Failed to preload region data for brand ${brand}:`, error)
        // Don't throw error - this is background preloading, shouldn't break the app
      }
    }

    // Preload in the background without blocking the UI
    preloadRegionData()
  }, [brand])

  return null
}
